import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, MeshReflectorMaterial } from "@react-three/drei";
import * as THREE from "three";
import { RotateCw, Layers, Box } from "lucide-react";
import { LIGHTBAR_MODELS, applyFlash, hexOf } from "./lightbarModels";

function StudioLightbar({ modelId, modules, activeFrame, clay }) {
  const meta = LIGHTBAR_MODELS.find((m) => m.id === modelId);
  const group = useMemo(() => {
    const g = meta.build(modules);
    const box = new THREE.Box3().setFromObject(g);
    const size = box.getSize(new THREE.Vector3());
    const s = meta.displaySize / Math.max(size.x, size.z);
    g.scale.setScalar(s);
    g.updateMatrixWorld(true);
    const box2 = new THREE.Box3().setFromObject(g);
    const ctr = box2.getCenter(new THREE.Vector3());
    g.position.set(-ctr.x, -box2.min.y, -ctr.z);
    const wrap = new THREE.Group();
    wrap.add(g);
    wrap.userData.lenses = g.userData.lenses;
    return wrap;
  }, [meta, modules]);
  useEffect(() => { applyFlash(group, activeFrame, modules, clay); }, [group, activeFrame, modules, clay]);
  return <primitive object={group} />;
}

function ReflectiveFloor() {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.06, 0]}>
      <planeGeometry args={[60, 60]} />
      <MeshReflectorMaterial
        blur={[280, 60]}
        resolution={1024}
        mixBlur={0.9}
        mixStrength={2.2}
        roughness={0.92}
        depthScale={1.1}
        minDepthThreshold={0.4}
        maxDepthThreshold={1.4}
        color="#070709"
        metalness={0.6}
        mirror={0.55}
      />
    </mesh>
  );
}

export default function LightbarViewport({ modules, activeFrame }) {
  const [model, setModel] = useState("fullbar");
  const [clay, setClay] = useState(false);
  const [autoRotate, setAutoRotate] = useState(true);
  const modelMeta = LIGHTBAR_MODELS.find((m) => m.id === model);

  return (
    <div data-testid="lightbar-3d-viewport" className="relative border border-white/10 bg-[#08080a] h-[460px] overflow-hidden">
      <Canvas camera={{ position: [6.5, 3.6, 8], fov: 40 }} gl={{ antialias: true }}>
        <color attach="background" args={["#08080a"]} />
        <fog attach="fog" args={["#08080a", 22, 48]} />
        <ambientLight intensity={0.4} />
        <directionalLight position={[6, 10, 4]} intensity={1.1} />
        <spotLight position={[-9, 8, -7]} intensity={55} angle={0.65} penumbra={0.9} color="#2E6BFF" />
        <spotLight position={[9, 6, 7]} intensity={30} angle={0.6} penumbra={0.9} color="#FF6B1A" />
        <StudioLightbar modelId={model} modules={modules} activeFrame={activeFrame} clay={clay} />
        <ReflectiveFloor />
        <OrbitControls makeDefault autoRotate={autoRotate} autoRotateSpeed={1.1} enablePan={false} minDistance={3.5} maxDistance={24} maxPolarAngle={Math.PI / 2.05} target={[0, 0.5, 0]} />
      </Canvas>

      <div className="absolute top-3 left-3 bg-black/70 backdrop-blur border border-white/10 px-3 py-2 flex items-center gap-2.5">
        <Box className="w-4 h-4 text-nc-blue" />
        <div>
          <p className="font-mono text-[10px] text-zinc-100">{modelMeta.label}</p>
          <p className="font-mono text-[8px] text-zinc-500">{modules.length} flash modules · {modelMeta.desc}</p>
        </div>
      </div>

      {/* model picker — right rail */}
      <div className="absolute top-3 right-3 flex flex-col gap-1.5 w-44 max-h-[380px] overflow-y-auto">
        {LIGHTBAR_MODELS.map((m) => (
          <button
            key={m.id}
            data-testid={`lightbar-model-${m.id}`}
            onClick={() => setModel(m.id)}
            className={`text-left border px-3 py-2 transition-colors backdrop-blur ${model === m.id ? "border-nc-blue bg-nc-blue/15 text-zinc-100" : "border-white/10 bg-black/60 text-zinc-500 hover:text-zinc-200 hover:border-white/30"}`}
          >
            <p className="font-mono text-[9px] uppercase tracking-[0.12em]">{m.label}</p>
            <p className="font-mono text-[7px] text-zinc-600 mt-0.5">{m.desc}</p>
          </button>
        ))}
      </div>

      {/* live module readout */}
      <div className="absolute bottom-14 left-3 hidden md:flex flex-col gap-1 max-h-40 overflow-y-auto">
        {modules.map((m, i) => (
          <div key={i} data-testid={`lightbar-module-row-${i}`} className="flex items-center gap-2 bg-black/60 border border-white/5 px-2 py-1">
            <span className="w-2 h-2 rounded-full transition-all" style={{ background: hexOf(m.color), boxShadow: activeFrame[i] ? `0 0 8px ${hexOf(m.color)}` : "none", opacity: activeFrame[i] ? 1 : 0.35 }} />
            <span className="font-mono text-[8px] text-zinc-400 uppercase">{m.label}</span>
            <span className={`font-mono text-[7px] ml-auto ${activeFrame[i] ? "text-nc-green" : "text-zinc-700"}`}>{activeFrame[i] ? "ON" : "—"}</span>
          </div>
        ))}
      </div>

      {/* bottom control bar */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/80 backdrop-blur border border-white/10 px-1.5 py-1.5">
        <button data-testid="lightbar-display-showroom" onClick={() => setClay(false)} className={`font-mono text-[8px] uppercase tracking-[0.15em] px-3 py-1.5 transition-colors flex items-center gap-1.5 ${!clay ? "bg-nc-blue text-white" : "text-zinc-500 hover:text-zinc-200"}`}>
          <Layers className="w-3 h-3" /> Showroom
        </button>
        <button data-testid="lightbar-display-clay" onClick={() => setClay(true)} className={`font-mono text-[8px] uppercase tracking-[0.15em] px-3 py-1.5 transition-colors ${clay ? "bg-nc-blue text-white" : "text-zinc-500 hover:text-zinc-200"}`}>
          Clay
        </button>
        <div className="w-px h-4 bg-white/10 mx-1" />
        <button data-testid="lightbar-autorotate" onClick={() => setAutoRotate((a) => !a)} className={`font-mono text-[8px] uppercase tracking-[0.15em] px-3 py-1.5 transition-colors flex items-center gap-1.5 ${autoRotate ? "text-nc-orange" : "text-zinc-500 hover:text-zinc-200"}`}>
          <RotateCw className={`w-3 h-3 ${autoRotate ? "animate-spin" : ""}`} style={{ animationDuration: "3s" }} /> Orbit
        </button>
      </div>
    </div>
  );
}
